import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/components/SEO";
import { calcEmi, inrFull, inrShort } from "@/lib/format";
import { MoneyInput, MoneyWords } from "@/components/MoneyInput";

const AnimatedMoney = ({ value, className, testId }) => {
  const spring = useSpring(value, { stiffness: 110, damping: 24 });
  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  const text = useTransform(spring, (v) => inrFull(v));
  return <motion.span data-testid={testId} className={className}>{text}</motion.span>;
};

function tenureLabel(months) {
  const m = Math.max(0, Math.round(months));
  const y = Math.floor(m / 12);
  const rem = m % 12;
  if (y === 0) return `${rem} months`;
  if (rem === 0) return `${y} ${y === 1 ? "year" : "years"}`;
  return `${y} yr ${rem} mo`;
}

export default function PrepaymentPage() {
  const [principal, setPrincipal] = useState(5000000);
  const [rate, setRate] = useState(7.4);
  const [months, setMonths] = useState(240);
  const [prepay, setPrepay] = useState(500000);
  const [afterMonths, setAfterMonths] = useState(24);

  useSEO({
    title: "Prepayment Calculator — How Part-Payment Cuts Interest & Tenure | Loan Salaah",
    description: "See how a part-prepayment on your loan reduces total interest and shortens your loan tenure. Instant, free, no sign-up.",
  });

  const principalNum = Number(principal) || 0;
  const prepayNum = Number(prepay) || 0;

  const r = useMemo(() => {
    const i = rate / 12 / 100;
    const n = Math.max(1, months);
    const { emi, totalInterest: interestA } = calcEmi(principalNum, rate, n);
    const k = Math.min(afterMonths, n - 1);

    // Balance after k months of EMI
    const growth = Math.pow(1 + i, k);
    const balanceK = i === 0 ? principalNum - emi * k : principalNum * growth - (emi * (growth - 1)) / i;

    const effectivePrepay = Math.min(prepayNum, Math.max(0, balanceK));
    const balance2 = balanceK - effectivePrepay;

    let m; // months to finish after prepayment, same EMI
    if (balance2 <= 0) {
      m = 0;
    } else if (i === 0) {
      m = balance2 / emi;
    } else {
      m = Math.log(emi / (emi - balance2 * i)) / Math.log(1 + i);
    }

    const interestBeforeK = emi * k - (principalNum - balanceK);
    const interestAfter = emi * m - balance2;
    const interestB = Math.max(0, interestBeforeK + Math.max(0, interestAfter));

    const interestSaved = Math.max(0, interestA - interestB);
    const newTotalMonths = k + m;
    const monthsSaved = Math.max(0, n - newTotalMonths);

    return { emi, interestA, interestB, interestSaved, newTotalMonths, monthsSaved, effectivePrepay, balanceK };
  }, [principalNum, rate, months, prepayNum, afterMonths]);

  return (
    <div data-testid="prepayment-page" className="pb-24 pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <div className="flex items-center gap-4">
            <span className="brass-rule" aria-hidden="true" />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-brass">Prepayment</span>
          </div>
          <h1 className="mt-5 font-serif text-4xl font-semibold tracking-tight text-concordia sm:text-5xl lg:text-6xl">
            One lump sum. Years off your loan.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-slate">
            A part-prepayment attacks your principal directly — your EMI stays the same, but the loan ends sooner
            and the interest saved is often startling. See your own numbers.
          </p>
        </Reveal>

        <Reveal delay={0.12} className="mt-12">
          <div className="grid overflow-hidden rounded-3xl border border-sand bg-white shadow-[0_30px_80px_rgba(30,74,61,0.1)] lg:grid-cols-5" data-testid="prepayment-calculator">
            {/* Controls */}
            <div className="space-y-10 p-6 sm:p-10 lg:col-span-3">
              <div>
                <div className="mb-3 flex items-end justify-between gap-4">
                  <label htmlFor="prepay-principal-input" className="text-sm font-medium text-slate">Loan amount</label>
                  <MoneyInput
                    id="prepay-principal-input"
                    testId="prepay-principal-input"
                    wordsTestId="prepay-principal-words"
                    value={principal}
                    max={100000000}
                    onChange={(v) => setPrincipal(v === "" ? "" : Math.min(100000000, v))}
                    onBlur={() => setPrincipal((a) => Math.min(100000000, Math.max(100000, Number(a) || 100000)))}
                  />
                </div>
                <Slider data-testid="prepay-principal-slider" value={[Math.min(50000000, Math.max(100000, principalNum || 100000))]} min={100000} max={50000000} step={100000}
                  onValueChange={([v]) => setPrincipal(v)} className="slider-brand" />
                <div className="mt-2 flex justify-between font-mono text-[0.65rem] uppercase tracking-wider text-slate/70">
                  <span>₹1 L</span><span>₹5 Cr</span>
                </div>
              </div>

              <div className="grid gap-10 sm:grid-cols-2 sm:gap-6">
                <div>
                  <div className="mb-3 flex items-end justify-between gap-3">
                    <label htmlFor="prepay-rate-input" className="text-sm font-medium text-slate">Interest rate</label>
                    <div className="flex items-center rounded-xl border border-sand bg-cream px-3 py-1.5 focus-within:border-brass">
                      <input id="prepay-rate-input" data-testid="prepay-rate-input" type="number" step="0.05"
                        value={rate} min={6} max={24}
                        onChange={(e) => setRate(Math.min(24, Math.max(6, Number(e.target.value) || 6)))}
                        className="w-16 bg-transparent text-right font-semibold text-concordia outline-none" />
                      <span className="ml-1 font-semibold text-concordia">%</span>
                    </div>
                  </div>
                  <Slider data-testid="prepay-rate-slider" value={[rate]} min={6} max={20} step={0.05}
                    onValueChange={([v]) => setRate(v)} className="slider-brand" />
                </div>
                <div>
                  <div className="mb-3 flex items-end justify-between gap-3">
                    <label htmlFor="prepay-tenure-input" className="text-sm font-medium text-slate">Tenure</label>
                    <div className="flex items-center rounded-xl border border-sand bg-cream px-3 py-1.5 focus-within:border-brass">
                      <input id="prepay-tenure-input" data-testid="prepay-tenure-input" type="number"
                        value={months} min={12} max={360}
                        onChange={(e) => setMonths(Math.min(360, Math.max(12, Math.round(Number(e.target.value) || 12))))}
                        className="w-16 bg-transparent text-right font-semibold text-concordia outline-none" />
                      <span className="ml-1 text-xs font-semibold text-slate">mo</span>
                    </div>
                  </div>
                  <Slider data-testid="prepay-tenure-slider" value={[months]} min={12} max={360} step={6}
                    onValueChange={([v]) => setMonths(v)} className="slider-brand" />
                </div>
              </div>

              <div className="grid gap-10 sm:grid-cols-2 sm:gap-6">
                <div>
                  <div className="mb-3 flex items-end justify-between gap-3">
                    <label htmlFor="prepay-amount-input" className="text-sm font-medium text-slate">Prepayment amount</label>
                    <MoneyInput
                      id="prepay-amount-input"
                      testId="prepay-amount-input"
                      wordsTestId="prepay-amount-words"
                      value={prepay}
                      max={principalNum || 100000000}
                      inputClass="w-24"
                      onChange={(v) => setPrepay(v === "" ? "" : Math.min(principalNum || 100000000, v))}
                      onBlur={() => setPrepay((a) => Math.min(principalNum, Math.max(10000, Number(a) || 10000)))}
                    />
                  </div>
                  <Slider data-testid="prepay-amount-slider" value={[Math.min(Math.max(100000, Math.round(principalNum / 2)), Math.max(10000, prepayNum))]} min={10000} max={Math.max(100000, Math.round(principalNum / 2))} step={10000}
                    onValueChange={([v]) => setPrepay(v)} className="slider-brand" />
                </div>
                <div>
                  <div className="mb-3 flex items-end justify-between gap-3">
                    <label htmlFor="prepay-after-input" className="text-sm font-medium text-slate">Prepay after</label>
                    <div className="flex items-center rounded-xl border border-sand bg-cream px-3 py-1.5 focus-within:border-brass">
                      <input id="prepay-after-input" data-testid="prepay-after-input" type="number"
                        value={afterMonths} min={1} max={months - 1}
                        onChange={(e) => setAfterMonths(Math.min(months - 1, Math.max(1, Math.round(Number(e.target.value) || 1))))}
                        className="w-16 bg-transparent text-right font-semibold text-concordia outline-none" />
                      <span className="ml-1 text-xs font-semibold text-slate">mo</span>
                    </div>
                  </div>
                  <Slider data-testid="prepay-after-slider" value={[afterMonths]} min={1} max={Math.max(2, months - 1)} step={1}
                    onValueChange={([v]) => setAfterMonths(v)} className="slider-brand" />
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="relative flex flex-col justify-between bg-concordia-ink p-6 text-ivory sm:p-10 lg:col-span-2">
              <div className="noise-overlay" aria-hidden="true" />
              <div className="relative">
                <p className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-brass">Interest you save</p>
                <AnimatedMoney value={r.interestSaved} testId="prepay-interest-saved"
                  className="mt-3 block text-4xl font-bold tracking-tight text-ivory sm:text-5xl" />
                <MoneyWords value={r.interestSaved} dark testId="prepay-saved-words" />
                <p className="mt-2 text-xs text-ivory/50">
                  by prepaying {inrShort(r.effectivePrepay)} after {afterMonths} months
                </p>

                <dl className="mt-8 space-y-4 text-sm">
                  <div className="flex items-center justify-between border-b border-ivory/10 pb-3">
                    <dt className="text-ivory/60">Your EMI (unchanged)</dt>
                    <dd className="text-right font-semibold" data-testid="prepay-emi">{inrFull(r.emi)}
                      <MoneyWords value={r.emi} suffix="Rupees per month" dark /></dd>
                  </div>
                  <div className="flex items-center justify-between border-b border-ivory/10 pb-3">
                    <dt className="text-ivory/60">Loan ends sooner by</dt>
                    <dd className="font-semibold text-sage" data-testid="prepay-time-saved">{tenureLabel(r.monthsSaved)}</dd>
                  </div>
                  <div className="flex items-center justify-between border-b border-ivory/10 pb-3">
                    <dt className="text-ivory/60">New tenure</dt>
                    <dd className="font-semibold" data-testid="prepay-new-tenure">{tenureLabel(r.newTotalMonths)}</dd>
                  </div>
                  <div className="flex items-center justify-between border-b border-ivory/10 pb-3">
                    <dt className="text-ivory/60">Interest without prepayment</dt>
                    <dd className="text-right font-semibold" data-testid="prepay-interest-before">{inrFull(r.interestA)}
                      <MoneyWords value={r.interestA} dark /></dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-ivory/60">Interest with prepayment</dt>
                    <dd className="text-right font-semibold text-brass" data-testid="prepay-interest-after">{inrFull(r.interestB)}
                      <MoneyWords value={r.interestB} dark /></dd>
                  </div>
                </dl>
              </div>

              <Link to={`/apply?amount=${principalNum}&rate=${rate}&tenure=${months}`} data-testid="prepay-apply-button"
                className="group relative mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-ivory px-7 py-4 text-sm font-semibold text-concordia transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(0,0,0,0.35)]">
                Discuss my loan with an advisor
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>

        <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate/80">
          Assumes the EMI stays unchanged and the tenure shortens — the most common prepayment mode. Some lenders
          charge prepayment fees on fixed-rate loans; floating-rate home loans to individuals are usually
          prepayment-fee-free. Your Loan Salaah advisor confirms the exact terms for your loan, free.
        </p>
      </div>
    </div>
  );
}
